import { config } from '@uhg-abyss/web/tools/config';
import { checkIsInternal } from '@src/utility/createStorage';
import httpService from '@src/utility/httpService';
const _httpService = new httpService();
import { HeaderConstants } from '@src/constants/header-constants';

type AcknowledgementResponse = {
  Data?: {
    AlertAcknowledged?: boolean;
    FileId?: string;
  };
};

type VideoUrlResponse = {
  url?: string;
  Url?: string;
  Data?: {
    url?: string;
    Url?: string;
  };
};

export const RELEASE_BANNER_STORAGE_KEYS = {
  legacy: 'ReleaseNotesAcknowledged',
  whatsNew: 'ReleaseNotesWhatsNewAcknowledged',
  fullNotes: 'ReleaseNotesFullNotesAcknowledged',
  closedByUser: 'ReleaseBannerClosedByUser',
} as const;

const normalizeReleaseNotesParam = (param: string) =>
  param.startsWith('/') ? param.slice(1) : param;

const getReleaseNotesUserId = () => {
  const loggedUser = JSON.parse(sessionStorage.getItem('loggedUser') || '{}');
  if (checkIsInternal()) {
    return loggedUser.MsId || '';
  }

  const currentUser = JSON.parse(sessionStorage.getItem('currentUser') || '[]');
  const user = Array.isArray(currentUser) ? currentUser[0] : currentUser;
  return (
    sessionStorage.getItem('Uuid') ||
    sessionStorage.getItem('uuid') ||
    user?.Uuid ||
    user?.uuid ||
    ''
  );
};

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL') || '');
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL') || '');
}

export const metricDefinitions = () => {
  const fetchData = async () => {
    try {
      const response = await _httpService.get<unknown>(`/metric-definitions/`);
      return response;
    } catch {
      return null;
    }
  };
  return fetchData();
};

export const getReleaseNotesService = (param) => {
  const fetchData = async () => {
    try {
      const normalizedParam = normalizeReleaseNotesParam(param);
      const httpOptions = {
        responseType: 'arraybuffer' as 'json',
      };
      const response = await _httpService.get<ArrayBuffer>(
        `/download-file/${normalizedParam}`,
        httpOptions
      );
      return response;
    } catch {
      return null;
    }
  };
  return fetchData();
};

export const getReleaseNotesVideoService = (param, requestOptions = {}) => {
  const fetchData = async () => {
    try {
      const normalizedParam = normalizeReleaseNotesParam(param);
      const httpOptions = {
        responseType: 'arraybuffer' as 'json',
        ...requestOptions,
      };
      const response = await _httpService.get<unknown>(
        `/download-file/${normalizedParam}`,
        httpOptions
      );
      return response;
    } catch {
      return null;
    }
  };
  return fetchData();
};

export const releaseNotesAcknowledgeSaveService = (payload) => {
  const postData = async () => {
    try {
      const response = await _httpService.push<unknown, typeof payload>(
        `/user/file-alert-ack-save`,
        payload
      );
      return response;
    } catch {
      return null;
    }
  };
  return postData();
};

export const releaseNotesAcknowledgeFetchService = (payload) => {
  const fetchData = async () => {
    try {
      const response = await _httpService.push<AcknowledgementResponse, typeof payload>(
        `user/file-alert-ack-fetch`,
        payload
      );
      return response;
    } catch {
      return null;
    }
  };
  return fetchData();
};

export const getFullReleaseNotesFeature = () => {
  return checkIsInternal()
    ? HeaderConstants.HeaderBanner.RELEASENOTESINTERNAL
    : HeaderConstants.HeaderBanner.RELEASENOTESEXTERNAL;
};

export const getWhatsNewVideoFeature = () => {
  return checkIsInternal()
    ? HeaderConstants.HeaderBanner.RELEASENOTESVIDEOINTERNAL
    : HeaderConstants.HeaderBanner.RELEASENOTESVIDEOEXTERNAL;
};

export const getFullReleaseNotesParam = () => `/${getFullReleaseNotesFeature()}`;

export const getWhatsNewVideoParam = () => `/${getWhatsNewVideoFeature()}`;

export const getFileAlertAcknowledgementFetch = async (
  featureOverride = getFullReleaseNotesFeature()
) => {
  const currentUser = sessionStorage.getItem('loggedUser');
  let isAcknowledged = false;

  if (currentUser) {
    const payload = {
      UserId: getReleaseNotesUserId(),
      Feature: featureOverride,
    };

    const response = await releaseNotesAcknowledgeFetchService(payload);
    if (response) {
      const fetchData = response.Data;
      if (response && fetchData) {
        const AlertAcknowledged = fetchData.AlertAcknowledged;
        isAcknowledged = Boolean(AlertAcknowledged);
      }
    }
    return {
      fileId: response!.Data!.FileId,
      isAcknowledged,
    };
  }

  return null;
};

export const getReleaseNote = async () => {
  const param = getFullReleaseNotesFeature();

  const file = await getReleaseNotesService(param);
  if (file) {
    const blob = new Blob([file], { type: 'application/pdf' });
    const fileURL = URL.createObjectURL(blob);
    window.open(fileURL);
    return true;
  } else {
    console.error('Failed to fetch release notes');
    return false;
  }
};

const toVideoObjectUrl = (payload: unknown) => {
  if (!payload) {
    return null;
  }

  if (typeof payload === 'string') {
    return payload;
  }

  if (payload instanceof Blob) {
    return URL.createObjectURL(payload);
  }

  if (payload instanceof ArrayBuffer) {
    return URL.createObjectURL(new Blob([payload], { type: 'video/mp4' }));
  }

  if (ArrayBuffer.isView(payload)) {
    const sourceBytes = new Uint8Array(payload.buffer, payload.byteOffset, payload.byteLength);
    const copiedBytes = new Uint8Array(sourceBytes.byteLength);
    copiedBytes.set(sourceBytes);
    return URL.createObjectURL(new Blob([copiedBytes.buffer], { type: 'video/mp4' }));
  }

  return null;
};

export const getWhatsNewOverview = async (options?: {
  featureOverride?: string;
  signal?: AbortSignal;
}) => {
  const feature = options?.featureOverride || getWhatsNewVideoFeature();
  const videoResponse = await getReleaseNotesVideoService(feature, {
    signal: options?.signal,
  });

  const objectUrl = toVideoObjectUrl(videoResponse);
  if (objectUrl) {
    return objectUrl;
  }

  if (typeof videoResponse === 'string') {
    return videoResponse;
  }
  if (typeof videoResponse === 'object' && videoResponse !== null) {
    const response = videoResponse as VideoUrlResponse;
    if (response.url) {
      return response.url;
    }
    if (response.Url) {
      return response.Url;
    }
    if (response.Data?.url) {
      return response.Data.url;
    }
    if (response.Data?.Url) {
      return response.Data.Url;
    }
  }
  return null;
};

export const getFileAlertAcknowledgementSave = async (
  fileId,
  setIsVisible,
  featureOverride = getFullReleaseNotesFeature(),
  storageKey: string = RELEASE_BANNER_STORAGE_KEYS.legacy,
  hideBanner = true
) => {
  const currentUser = sessionStorage.getItem('loggedUser');
  let isVisibleFlag = !hideBanner;
  let didSave = false;
  if (currentUser) {
    const payload = {
      UserId: getReleaseNotesUserId(),
      Feature: featureOverride,
      FileId: fileId,
    };
    try {
      const response = await releaseNotesAcknowledgeSaveService(payload);
      if (response) {
        didSave = true;
        if (hideBanner) {
          isVisibleFlag = false;
        }
      }
    } catch (error) {
      console.error('Failed to save release notes acknowledgement', error);
    }
    if (setIsVisible) {
      if (isVisibleFlag) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    }
    localStorage.setItem(storageKey, isVisibleFlag ? 'false' : 'true');
    if (hideBanner && !isVisibleFlag) {
      // Set the height of the toolbar to 38px
      const id = document.getElementsByClassName('example-toolbar')[0] as HTMLElement;
      if (id) {
        id.style.height = '38px';
      }
    }
  }

  return didSave;
};

export const fetchReleaseBannerAckStatus = async () => {
  const fullNotesResponse = await getFileAlertAcknowledgementFetch(getFullReleaseNotesFeature());

  return {
    fullNotesFileId: fullNotesResponse?.fileId,
    isFullNotesAcknowledged: Boolean(fullNotesResponse?.isAcknowledged),
  };
};

export const closeReleaseBannerByUser = () => {
  sessionStorage.setItem(RELEASE_BANNER_STORAGE_KEYS.closedByUser, 'true');
};
